// "Images blocked" preview for the email canvas.
//
// Many email clients (Outlook desktop, Gmail/Apple Mail in various privacy states) do NOT
// load images until the recipient opts in — so a large share of readers first see the email
// as text with empty image slots. This lets an author preview that state inside the builder.
//
// IMPLEMENTATION: canvas-only. It injects a <style> into the GrapesJS canvas iframe that
// neutralises images (dim + desaturate + dashed placeholder box). It deliberately does NOT
// touch the component model / image `src`, because getHtml()/getComponents() serialize the
// MODEL — mutating `src` would risk persisting a blocked state if the user saved while the
// preview was on. Because this is pure canvas CSS, saved html/components are never affected
// and the toggle is always cleanly reversible.

export const STYLE_ID = 'wm-toggle-images-style';

// Dim + grayscale the picture and outline the box so the author sees where images sit and
// what the layout looks like without them. `!important` beats per-image inline styles.
export const BLOCKED_CSS =
  'img{opacity:0.12 !important;filter:grayscale(100%) !important;' +
  'outline:1px dashed #b3b3b3 !important;outline-offset:-1px;' +
  'background-color:#f6f6f6 !important;}';

const getHead = (doc) =>
  doc.head ||
  (doc.getElementsByTagName && doc.getElementsByTagName('head')[0]) ||
  doc.body;

// Inject the blocked-images stylesheet into the canvas document. Idempotent.
export const applyBlockedImages = (doc) => {
  if (!doc || typeof doc.getElementById !== 'function') return false;
  if (doc.getElementById(STYLE_ID)) return false; // already applied
  const style = doc.createElement('style');
  style.id = STYLE_ID;
  style.innerHTML = BLOCKED_CSS;
  const head = getHead(doc);
  if (!head) return false;
  head.appendChild(style);
  return true;
};

// Remove the blocked-images stylesheet. Idempotent (no-op if not applied).
export const restoreImages = (doc) => {
  if (!doc || typeof doc.getElementById !== 'function') return false;
  const style = doc.getElementById(STYLE_ID);
  if (!style) return false;
  if (style.parentNode) style.parentNode.removeChild(style);
  else if (typeof style.remove === 'function') style.remove();
  return true;
};
